"use client";

import {
    MARK_SURVEY_VIEWED,
    SUBMIT_SURVEY_RESPONSE,
} from "@/graphql/mutations/surveyMutations";
import { SURVEY_DETAIL_QUERY } from "@/graphql/queries/surveyQueries";
import { useMutation, useQuery } from "@apollo/client";
import { CheckCircleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

interface QuestionOption {
    id: string;
    text: string;
    order: number;
}

interface Question {
    id: string;
    text: string;
    questionType: string;
    isRequired: boolean;
    order: number;
    imageUrl: string | null;
    videoUrl: string | null;
    conditionalOnId: string | null;
    conditionalValue: string | null;
    options: QuestionOption[];
    images: { id: string; imageUrl: string; order: number }[];
}

interface Survey {
    id: string;
    title: string;
    description: string;
    isAnonymous: boolean;
    vpReward: number;
    status: string;
    imageUrl: string | null;
    videoUrl: string | null;
    myResponse: { id: string; vpAwarded: boolean } | null;
    questions: Question[];
}

function getYoutubeEmbedUrl(url: string): string | null {
    try {
        const parsed = new URL(url);
        let videoId: string | null = null;
        if (parsed.hostname.includes("youtube.com")) {
            videoId = parsed.searchParams.get("v");
        } else if (parsed.hostname.includes("youtu.be")) {
            videoId = parsed.pathname.slice(1);
        }
        return videoId
            ? `https://www.youtube-nocookie.com/embed/${videoId}`
            : null;
    } catch {
        return null;
    }
}

export default function SurveyPage() {
    const { id } = useParams<{ id: string }>();
    const [answers, setAnswers] = useState<Record<string, string | string[]>>(
        {},
    );
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [vpEarned, setVpEarned] = useState(0);
    const [errorMsg, setErrorMsg] = useState("");

    const { data, loading, error } = useQuery(SURVEY_DETAIL_QUERY, {
        variables: { id },
    });

    const [markViewed] = useMutation(MARK_SURVEY_VIEWED);
    const [submitResponse] = useMutation(SUBMIT_SURVEY_RESPONSE);

    // Mark as viewed on mount
    useEffect(() => {
        if (id) {
            markViewed({ variables: { surveyId: id } });
        }
    }, [id, markViewed]);

    if (loading) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="border-brand h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                    Error loading survey: {error.message}
                </div>
            </div>
        );
    }

    const survey: Survey | null = data?.survey ?? null;
    if (!survey) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-slate-600 dark:text-slate-400">
                        Survey not found.
                    </p>
                </div>
            </div>
        );
    }

    // Survey is closed
    if (survey.status === "closed") {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="max-w-2xl space-y-4 rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <LockClosedIcon className="mx-auto h-16 w-16 text-slate-400" />
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        Survey Closed
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400">
                        This survey is no longer accepting responses.
                    </p>
                    <Link
                        to="/dashboard/surveys"
                        className="bg-brand hover:bg-brand/90 mt-2 inline-block rounded-lg px-6 py-2 font-medium text-white transition-colors"
                    >
                        Back to Surveys
                    </Link>
                </div>
            </div>
        );
    }

    // Already completed
    if (survey.myResponse || submitted) {
        return (
            <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="max-w-2xl space-y-4 rounded-2xl border border-slate-300 bg-white p-8 text-center shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <CheckCircleIcon className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" />
                    <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                        {submitted
                            ? "Response Submitted!"
                            : "Already Completed"}
                    </h2>
                    {vpEarned > 0 && (
                        <p className="text-brand text-lg font-semibold">
                            +{vpEarned} VP earned!
                        </p>
                    )}
                    <p className="text-slate-600 dark:text-slate-400">
                        Thank you for your feedback.
                    </p>
                    <Link
                        to="/dashboard"
                        className="bg-brand hover:bg-brand/90 mt-2 inline-block rounded-lg px-6 py-2 font-medium text-white transition-colors"
                    >
                        Back to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    const setAnswer = (questionId: string, value: string | string[]) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const isQuestionVisible = (q: Question): boolean => {
        if (!q.conditionalOnId || !q.conditionalValue) return true;
        const parentAnswer = answers[q.conditionalOnId];
        if (!parentAnswer) return false;

        // For MC/checkbox, answers are option IDs — resolve to text
        const parentQuestion = survey.questions.find(
            (pq) => pq.id === q.conditionalOnId,
        );
        if (
            parentQuestion &&
            (parentQuestion.questionType === "multiple_choice" ||
                parentQuestion.questionType === "checkbox")
        ) {
            if (Array.isArray(parentAnswer)) {
                return parentAnswer.some((optId) => {
                    const opt = parentQuestion.options.find(
                        (o) => o.id === optId,
                    );
                    return opt?.text === q.conditionalValue;
                });
            }
            const selectedOpt = parentQuestion.options.find(
                (o) => o.id === parentAnswer,
            );
            return selectedOpt?.text === q.conditionalValue;
        }

        // For text/number/rating, compare directly
        if (Array.isArray(parentAnswer)) {
            return parentAnswer.includes(q.conditionalValue);
        }
        return parentAnswer === q.conditionalValue;
    };

    const visibleQuestions = survey.questions
        .slice()
        .sort((a, b) => a.order - b.order)
        .filter(isQuestionVisible);

    const handleSubmit = async () => {
        setErrorMsg("");

        // Validate required
        for (const q of visibleQuestions) {
            if (!q.isRequired) continue;
            const ans = answers[q.id];
            if (
                !ans ||
                (Array.isArray(ans) && ans.length === 0) ||
                (typeof ans === "string" && !ans.trim())
            ) {
                setErrorMsg(`Please answer required question: "${q.text}"`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const formattedAnswers = visibleQuestions
                .filter((q) => answers[q.id] !== undefined)
                .map((q) => {
                    const val = answers[q.id];
                    const isOptionQuestion =
                        q.questionType === "multiple_choice" ||
                        q.questionType === "checkbox";
                    return JSON.stringify({
                        question_id: q.id,
                        text_answer:
                            !isOptionQuestion && typeof val === "string"
                                ? val
                                : null,
                        number_answer:
                            q.questionType === "number" ||
                            q.questionType === "rating"
                                ? parseFloat(
                                      typeof val === "string" ? val : "0",
                                  )
                                : null,
                        selected_option_ids: isOptionQuestion
                            ? Array.isArray(val)
                                ? val
                                : [val]
                            : null,
                    });
                });

            const result = await submitResponse({
                variables: {
                    surveyId: survey.id,
                    answers: formattedAnswers,
                },
            });

            setVpEarned(result.data?.submitSurveyResponse?.vpEarned ?? 0);
            setSubmitted(true);
        } catch (err) {
            setErrorMsg(
                err instanceof Error
                    ? err.message
                    : "Failed to submit response.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const surveyEmbedUrl = survey.videoUrl
        ? getYoutubeEmbedUrl(survey.videoUrl)
        : null;

    return (
        <div className="fixed inset-0 z-10 overflow-y-auto bg-slate-950/80 pt-16 backdrop-blur-sm">
            <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
                <div className="space-y-3">
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        {survey.title}
                    </h1>
                    {survey.description && (
                        <p className="text-slate-600 dark:text-slate-400">
                            {survey.description}
                        </p>
                    )}
                    {survey.vpReward > 0 && (
                        <p className="text-brand text-sm font-semibold">
                            Earn {survey.vpReward} VP for completing this survey
                        </p>
                    )}
                    {survey.isAnonymous && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Your responses are anonymous.
                        </p>
                    )}
                </div>

                {/* Survey-level media */}
                {survey.imageUrl && (
                    <img
                        src={survey.imageUrl}
                        alt=""
                        className="w-full rounded-lg object-cover"
                    />
                )}
                {surveyEmbedUrl && (
                    <div className="aspect-video overflow-hidden rounded-lg">
                        <iframe
                            src={surveyEmbedUrl}
                            className="h-full w-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Survey video"
                        />
                    </div>
                )}

                {/* Questions */}
                <div className="space-y-4">
                    {visibleQuestions.map((q) => (
                        <QuestionInput
                            key={q.id}
                            question={q}
                            value={answers[q.id]}
                            onChange={(val) => setAnswer(q.id, val)}
                        />
                    ))}
                </div>

                {errorMsg && (
                    <div className="rounded-lg bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
                        {errorMsg}
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-brand hover:bg-brand/90 w-full rounded-lg py-3 font-medium text-white transition-colors disabled:opacity-50"
                >
                    {submitting ? "Submitting..." : "Submit Response"}
                </button>
            </div>
        </div>
    );
}

function QuestionInput({
    question,
    value,
    onChange,
}: {
    question: Question;
    value: string | string[] | undefined;
    onChange: (val: string | string[]) => void;
}) {
    const questionEmbedUrl = question.videoUrl
        ? getYoutubeEmbedUrl(question.videoUrl)
        : null;

    return (
        <div className="space-y-2 rounded-2xl border border-slate-300 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-900">
            <label className="block font-medium text-slate-900 dark:text-slate-100">
                {question.text}
                {question.isRequired && (
                    <span className="ml-1 text-red-600 dark:text-red-400">
                        *
                    </span>
                )}
            </label>

            {question.imageUrl && (
                <img
                    src={question.imageUrl}
                    alt=""
                    className="max-h-60 rounded-lg object-cover"
                />
            )}
            {question.images?.length > 0 && (
                <div className="flex flex-wrap gap-3">
                    {question.images.map((img) => (
                        <img
                            key={img.id}
                            src={img.imageUrl}
                            alt=""
                            className="max-h-60 rounded-lg object-cover"
                        />
                    ))}
                </div>
            )}
            {questionEmbedUrl && (
                <div className="aspect-video overflow-hidden rounded-lg">
                    <iframe
                        src={questionEmbedUrl}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Question video"
                    />
                </div>
            )}

            {question.questionType === "text_short" && (
                <input
                    type="text"
                    value={(value as string) ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    placeholder="Your answer..."
                />
            )}

            {question.questionType === "text_long" && (
                <textarea
                    value={(value as string) ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    placeholder="Your answer..."
                />
            )}

            {question.questionType === "number" && (
                <input
                    type="number"
                    value={(value as string) ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                    placeholder="0"
                />
            )}

            {question.questionType === "rating" && (
                <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            onClick={() => onChange(String(n))}
                            className={`flex h-10 w-10 items-center justify-center rounded-lg border text-sm font-semibold transition ${
                                value === String(n)
                                    ? "bg-brand border-brand text-white"
                                    : "border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400"
                            }`}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            )}

            {question.questionType === "multiple_choice" && (
                <div className="space-y-2">
                    {question.options
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((opt) => (
                            <label
                                key={opt.id}
                                className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 transition hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-slate-500"
                            >
                                <input
                                    type="radio"
                                    name={`q-${question.id}`}
                                    value={opt.id}
                                    checked={value === opt.id}
                                    onChange={() => onChange(opt.id)}
                                    className="text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-slate-700 dark:text-slate-300">
                                    {opt.text}
                                </span>
                            </label>
                        ))}
                </div>
            )}

            {question.questionType === "checkbox" && (
                <div className="space-y-2">
                    {question.options
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((opt) => {
                            const selected = (value as string[]) ?? [];
                            const isChecked = selected.includes(opt.id);
                            return (
                                <label
                                    key={opt.id}
                                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 transition hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:hover:border-slate-500"
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                            const newSelected = isChecked
                                                ? selected.filter(
                                                      (s) => s !== opt.id,
                                                  )
                                                : [...selected, opt.id];
                                            onChange(newSelected);
                                        }}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-slate-700 dark:text-slate-300">
                                        {opt.text}
                                    </span>
                                </label>
                            );
                        })}
                </div>
            )}
        </div>
    );
}
